import { Button } from '@affine/component';
import {
  SettingsMeter,
  SettingsRow,
} from '@affine/component/setting-components';
import { settingsColor } from '@affine/component/setting-components/tokens';
import { useI18n } from '@affine/i18n';
import { useState } from 'react';

import { EmbeddingDocIndexDialog } from './embedding-doc-index-dialog';
import * as styles from './styles.css';

interface EmbeddingProgressProps {
  status: { embedded: number; total: number } | null;
  workspaceId: string;
}

/** How much of the workspace the search index covers, with the page list behind it. */
export const EmbeddingProgress = ({
  status,
  workspaceId,
}: EmbeddingProgressProps) => {
  const t = useI18n();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Embeddings of deleted pages can outlive them, so the count is capped.
  const embedded = status ? Math.min(status.embedded, status.total) : 0;
  const share = !status ? 0 : status.total === 0 ? 1 : embedded / status.total;
  const state = !status ? 'loading' : share >= 1 ? 'synced' : 'syncing';
  const title =
    t[
      'com.streamlain.settings.workspace.indexer-embedding.embedding.progress.title'
    ]();

  return (
    <>
      <SettingsRow
        data-testid="embedding-progress-wrapper"
        label={title}
        description={
          <span data-testid="embedding-progress-title" data-progress={state}>
            {state === 'loading'
              ? t[
                  'com.streamlain.settings.workspace.indexer-embedding.embedding.progress.loading-sync-status'
                ]()
              : state === 'synced'
                ? t[
                    'com.streamlain.settings.workspace.indexer-embedding.embedding.progress.synced'
                  ]()
                : t[
                    'com.streamlain.settings.workspace.indexer-embedding.embedding.progress.syncing'
                  ]()}
          </span>
        }
      >
        {status ? (
          <span className={styles.progressAside}>
            <span data-testid="embedding-progress-count">
              {`${embedded}/${status.total}`}
            </span>
            {status.total > 0 ? (
              <Button
                data-testid="embedding-progress-details-toggle"
                onClick={() => setDialogOpen(true)}
              >
                {t[
                  'com.streamlain.settings.workspace.indexer-embedding.doc-index.open'
                ]()}
              </Button>
            ) : null}
          </span>
        ) : null}
      </SettingsRow>
      {status ? (
        <div className={styles.progressBar} data-testid="embedding-progress">
          <SettingsMeter
            label={title}
            legend={false}
            segments={[
              {
                id: 'indexed',
                label: title,
                share,
                color: settingsColor.brand,
              },
            ]}
          />
        </div>
      ) : null}
      <EmbeddingDocIndexDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        workspaceId={workspaceId}
      />
    </>
  );
};
